import { Call } from '../../../utils/multicall'
import {
  isAaveV2Type,
  isAaveV3Type,
  isAaveV4Type,
  isCompoundV2Type,
  isCompoundV3Type,
  isEulerType,
  isFluid,
  isGearboxV3,
  isInit,
  isMidnight,
  isTerm,
  isExactly,
  isLiquityFamily,
  isRiver,
  isTeller,
  isTermMax,
  isInverse,
  isCooler,
  isFlyingTulip,
  isLlamaLend,
  isResupply,
  isFraxlend,
  isCurvance,
  isTwyne,
  isDssType,
  isFrankencoin,
  isMorphoType,
  isSiloV2Type,
  isSiloV3Type,
  isDolomite,
} from '../../../utils'
import { buildAaveV2UserCall } from '../aave-v2-type/userCallBuild'
import { buildAaveV3UserCall } from '../aave-v3-type/userCallBuild'
import { buildInitUserCall } from '../init/userCallBuild'
import { buildCompoundV3UserCall } from '../compound-v3/userCallBuild'
import { buildMorphoTypeUserCallWithLens } from '../morpho/userCallBuild'
import { buildCompoundV2UserCall } from '../compound-v2'
import { buildEulerUserCall } from '../euler/userCallBuild'
import { buildAaveV4UserCall } from '../aave-v4-type/userCallBuild'
import { buildSiloV2UserCall } from '../silo-v2/userCallBuild'
import { buildSiloV3UserCall } from '../silo-v3/userCallBuild'
import { buildFluidUserCall } from '../fluid/userCallBuild'
import { buildGearboxV3UserCall } from '../gearbox/userCallBuild'
import { buildDolomiteUserCall } from '../dolomite/userCallBuild'
import { buildMidnightUserCall } from '../midnight/userCallBuild'
import { buildTermUserCall } from '../term/userCallBuild'
import { buildExactlyUserCall } from '../exactly/userCallBuild'
import { buildLiquityUserCall } from '../liquity/userCallBuild'
import { buildRiverUserCall } from '../river/userCallBuild'
import { buildLlamaLendUserCall } from '../llamalend/userCallBuild'
import { buildInverseUserCall } from '../inverse/userCallBuild'
import { buildCoolerUserCall } from '../cooler/userCallBuild'
import { buildResupplyUserCall } from '../resupply/userCallBuild'
import { buildFraxlendUserCall } from '../fraxlend/userCallBuild'
import { buildCurvanceUserCall } from '../curvance/userCallBuild'
import { buildTwyneUserCall } from '../twyne/userCallBuild'
import { buildDssUserCall } from '../dss/userCallBuild'
import { buildFrankencoinUserCall } from '../frankencoin/userCallBuild'
import { buildTellerUserCall } from '../teller/userCallBuild'
import { buildFlyingTulipUserCall } from '../flying-tulip/userCallBuild'
import { buildTermMaxUserCall } from '../termmax/userCallBuild'

export async function buildUserCall(
  chainId: string,
  lender: string,
  account: string,
  params?: any,
  getClient?: (chainId: string, rpcId?: number) => any,
): Promise<Call[]> {
  if (isAaveV4Type(lender)) return buildAaveV4UserCall(chainId, lender, account)
  if (isAaveV2Type(lender)) return buildAaveV2UserCall(chainId, lender, account)
  if (isAaveV3Type(lender)) return buildAaveV3UserCall(chainId, lender, account)
  if (isInit(lender)) return buildInitUserCall(chainId, lender, account)
  if (isMorphoType(lender))
    return buildMorphoTypeUserCallWithLens(
      chainId,
      account,
      lender,
      params,
      getClient,
    )
  if (isMidnight(lender)) return buildMidnightUserCall(chainId, lender, account)
  if (isTerm(lender)) return buildTermUserCall(chainId, lender, account)
  if (isExactly(lender)) return buildExactlyUserCall(chainId, lender, account)
  // Async: runs its own trove-discovery multicall first (ids are probed —
  // TroveNFT is not enumerable), then returns the per-trove data calls.
  if (isLiquityFamily(lender))
    return buildLiquityUserCall(chainId, lender, account)
  // River troves are address-keyed — no discovery; layout is config-static.
  if (isRiver(lender)) return buildRiverUserCall(chainId, lender, account)
  // Async: discovers the borrower's bidIds (getBorrowerActiveLoanIds) and
  // attributes each to a curated pool before returning the per-bid data calls.
  if (isTeller(lender)) return buildTellerUserCall(chainId, lender, account)
  // Async: TermMax markets are discovered dynamically (no static registry —
  // they churn on every maturity roll), and borrow positions are GT ERC-721
  // sub-accounts. One `TermMaxViewer.getPositionDetails` call covers both.
  if (isTermMax(lender)) return buildTermMaxUserCall(chainId, lender, account)
  // Inverse FiRM escrows are address-keyed (local CREATE2 predict) — no
  // discovery; layout is config-static.
  if (isInverse(lender)) return buildInverseUserCall(chainId, lender, account)
  // Cooler positions are address-keyed on ONE monolithic contract — no
  // discovery; the whole read is a single `accountPosition(account)` call.
  if (isCooler(lender)) return buildCoolerUserCall(chainId, lender, account)
  // Flying Tulip is cross-margin and address-keyed — ONE lens call returns
  // both legs plus the protocol's own equity/maint/HF aggregate.
  if (isFlyingTulip(lender))
    return buildFlyingTulipUserCall(chainId, lender, account)
  // LlamaLend loans are address-keyed and the Controller caps them at one per
  // address per market, so there is no discovery phase — the layout is
  // roster-static, like Inverse's.
  if (isLlamaLend(lender))
    return buildLlamaLendUserCall(chainId, lender, account)
  // Resupply positions are address-keyed (one per user per pair, no ids), but
  // the pair roster comes from an on-chain registry read, so this build is
  // async like Teller's/Frankencoin's discovery-first builds.
  if (isResupply(lender)) return buildResupplyUserCall(chainId, lender, account)
  if (isFraxlend(lender)) return buildFraxlendUserCall(chainId, lender, account)
  // Curvance positions are address-keyed (one per user per market). Discovery
  // first via `assetsOf`, so the shared batch only carries markets the user is
  // actually in — 25 markets x 2 legs would otherwise be 150 dead calls.
  if (isCurvance(lender)) return buildCurvanceUserCall(chainId, lender, account)
  // Twyne positions are per-borrower CONTRACTS (many per user per market), so
  // the build phase discovers them via `factory.getCollateralVaults(account)`
  // and resolves each vault's market before emitting any state read. A user
  // with no Twyne position costs exactly one call.
  if (isTwyne(lender)) return buildTwyneUserCall(chainId, lender, account)
  // Async: walks the DssCdpManager per-owner CDP list (many CDPs per user
  // per ilk) before returning the per-urn data calls — Teller pattern.
  if (isDssType(lender)) return buildDssUserCall(chainId, lender, account)
  // Async: one cached fetch of Frankencoin's whole open book resolves every
  // account's position contracts (no RPC), then per-position data calls.
  if (isFrankencoin(lender))
    return buildFrankencoinUserCall(chainId, lender, account)
  if (isCompoundV3Type(lender))
    return buildCompoundV3UserCall(chainId, lender, account)
  if (isEulerType(lender))
    return buildEulerUserCall(
      chainId,
      lender,
      account,
      params?.subAccountIndexes,
    )
  // Silo queries are COLLAPSED upstream (`organizeUserQueries`): one query per
  // version carrying every pair key in `params`, so a single lens call covers the
  // roster instead of one per pair. `params` absent means a caller named a single
  // pair key — still supported, still one pair.
  if (isSiloV2Type(lender))
    return buildSiloV2UserCall(chainId, lender, account, params)
  if (isSiloV3Type(lender))
    return buildSiloV3UserCall(chainId, lender, account, params)
  if (isCompoundV2Type(lender))
    return buildCompoundV2UserCall(chainId, lender, account)
  if (isFluid(lender)) return buildFluidUserCall(chainId, lender, account)
  if (isGearboxV3(lender))
    return buildGearboxV3UserCall(chainId, lender, account)
  if (isDolomite(lender))
    return buildDolomiteUserCall(
      chainId,
      lender,
      account,
      params?.accountNumbers,
    )
  // Unknown lender type — return empty to avoid ABI errors
  return []
}
